import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';
import {useTable, usePagination, useFilters} from 'react-table';
import {Table, Button, Dropdown, Input, Icon} from 'semantic-ui-react';
import {validateTypeInvoice} from '../../utils';
import {paths} from '../../../common/dictionaries/urls';
import {invoicesPageSizes} from '../../../common/constants/lists';
import {Filter, DefaultColumnFilter, SelectColumnFilter} from './TableFilters';

const columnsTable = [
  {
    'Header': 'DOCUMENTOS',
    'accessor': 'route',
    'Filter': SelectColumnFilter,
    'filter': 'includes',
    'Cell': ({row}) => {
      const {route} = row.original;
      return validateTypeInvoice(route);
    },
  },
  {
    'Header': 'TIPO',
    'accessor': 'documentType',
    'Filter': SelectColumnFilter,
    'filter': 'includes',
    'Cell': ({row}) => {
      const {documentType} = row.original;
      return documentType === 'FC' ? 'FACTURA' : 'NOTA DE CRÉDITO';
    },
  },
  {
    'Header': 'FECHA',
    'accessor': 'date',
    'Cell': ({row}) => {
      const {castDate} = row.original;
      return castDate;
    },
  },
  {
    'Header': 'AGENCIA',
    'accessor': 'name',
  },
  {
    'Header': 'NUM.FAC',
    'accessor': 'invoiceNumber',
  },
  {
    'Header': 'SERIE',
    'accessor': 'serial',
  },
  {
    'Header': 'FAC/NC',
    'accessor': 'physicalNumber',
  },
  {
    'Header': 'SUBTOTAL',
    'accessor': 'subtotal',
  },
  {
    'Header': 'IVA',
    'accessor': 'tax',
  },
  {
    'Header': 'TOTAL',
    'accessor': 'total',
  },
  {
    'Header': 'VER',
    'accessor': 'viewTwc',
    'width': 100,
    'Cell': ({row}) => {
      const {documentType} = row?.original;
      return (
        <Link
          to={paths.FileInvoices}
          state={{...row.original, 'type': documentType}}
        >
          {' '}
          <Icon name='eye' size='large' />{' '}
        </Link>
      );
    },
  },
];


const InvoiceTable = (props) => {
  const {isUserFromTDE, dataTable} = props;
  const isUserInColumns = columnsTable.some((header) => header.Header === 'USUARIO');
  if (isUserFromTDE && !isUserInColumns) {
    columnsTable.push({
      'Header': 'USUARIO',
      'accessor': 'user',
    });
  }

  const data = useMemo(() => dataTable, []);
  const columns = useMemo(() => columnsTable, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 10},
    },
    useFilters,
    usePagination,
  );

  return (
    <Table className='interactive' compact striped {...getTableProps()}>
      <Table.Header>
        {headerGroups.map((headerGroup) => (
          <Table.Row key='headers' {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, index) => (
              <Table.HeaderCell
                key={`title-${index}`}
                textAlign='center'
                {...column.getHeaderProps()}
              >
                {column.render('Header')}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        ))}
        {headerGroups.map((headerGroup) => (
          <Table.Row key='filters' id='filters'>
            {headerGroup.headers.map((column, index) => (
              <Table.HeaderCell
                key={`filter-${index}`}
                className={index < 2 ? 'dropdown-header' : ''}
                textAlign='center'
              >
                <Filter column={column} />
              </Table.HeaderCell>
            ))}
          </Table.Row>
        ))}
      </Table.Header>
      <Table.Body {...getTableBodyProps()}>
        {!page.length && (
          <Table.Row>
            <Table.Cell colSpan={12} textAlign='center'>
              No hay datos disponibles
            </Table.Cell>
          </Table.Row>
        )}
        {page.length > 0 &&
          page.map((row, i) => {
            prepareRow(row);
            return (
              <Table.Row key={i} {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <Table.Cell textAlign='center' {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })}
      </Table.Body>
      {page.length > 0 && (
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={2} textAlign='left'>
              <Button
                className='navigation-button'
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                content='Anterior'
              />
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={4} textAlign='right'>
              <span>páginas </span>
              <span>
                <Input
                  className='pages-input'
                  type='number'
                  value={pageIndex + 1}
                  min='1'
                  max={pageOptions.length}
                  onChange={(e, {value}) => {
                    const page = value ? Number(value) - 1 : 0;
                    if (page < 1 || page > pageOptions.length) {
                      e.target.value = 1;
                    }
                    gotoPage(page);
                  }}
                />
              </span>{' '}
              <span>de {pageOptions.length}</span>
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={isUserInColumns ? 4 : 3} textAlign='left'>
              <Dropdown
                className='page-sizes-selection'
                selection
                upward
                compact
                value={pageSize}
                options={invoicesPageSizes}
                onChange={(e, {value}) => {
                  setPageSize(Number(value));
                }}
              />
              <span>filas</span>
            </Table.HeaderCell>
            <Table.HeaderCell colSpan={2} textAlign='right'>
              <Button
                className='navigation-button'
                onClick={() => nextPage()}
                disabled={!canNextPage}
                content='Siguiente'
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      )}
    </Table>
  );
};

export default InvoiceTable;
